import {
  ActionError,
  AttentionBlock,
  Button,
  Checkbox,
  ChoiceRow,
  ConfirmDialog,
  Field,
  FieldGrid,
  FormBlock,
  FormFooter,
  Input,
  Radio,
  RadioGroup,
  Select,
  toast,
} from '@westhetics/ui'
import { useState, type FormEvent } from 'react'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useUpdateTemplate, type Audience, type Category, type Template } from './api'
import { isRequiredCategory } from './clinic-activation'
import { AUDIENCE_CATEGORIES, AUDIENCE_LABELS, CATEGORY_LABELS, CATEGORY_TYPE } from './labels'

/**
 * Aba Dados: o que identifica o documento fora do texto (docs/areas/documentos-e-assinaturas.md, "Documento",
 * Proposta P-DOC-43). Nome, procedimento impresso no termo, público, categoria, fluxo e padrão do catálogo, pelo
 * `PUT /documents/templates/:id` (RN-DOC-42, RN-DOC-02, RN-DOC-05). Arquétipo cadastro dentro da ficha: Salvar e
 * Cancelar no rodapé do formulário (T35). O texto do documento é da aba Editor.
 */

const AUDIENCES = Object.keys(AUDIENCE_LABELS) as Audience[]
/** Categorias em que a clínica tem um documento só: marcar outro como padrão tira a marca do atual (RN-DOC-06). */
const EXCLUSIVE: Category[] = ['anamnesis', 'service_contract', 'model_patient_contract']

interface Values {
  name: string
  procedure: string
  audience: Audience
  category: Category
  /** Só o termo avulso de Pacientes escolhe: também no fluxo de procedimento. */
  alsoInProcedure: boolean
  isDefault: boolean
}

function valuesOf(template: Template): Values {
  return {
    name: template.name,
    procedure: template.procedure ?? '',
    audience: template.audience,
    category: template.category,
    alsoInProcedure: template.selectableIn.includes('procedure'),
    isDefault: template.isDefault,
  }
}

export function TemplateDataTab({ template }: { template: Template }) {
  // O formulário nasce do que está salvo; depois de salvar, remonta com o novo.
  return (
    <DataForm
      key={[
        template.name,
        template.procedure,
        template.audience,
        template.category,
        template.selectableIn.join(','),
        template.isDefault,
        template.state,
      ].join('|')}
      template={template}
    />
  )
}

function DataForm({ template }: { template: Template }) {
  const update = useUpdateTemplate(template.id)
  const saved = valuesOf(template)
  const [values, setValues] = useState<Values>(saved)
  const [submitted, setSubmitted] = useState(false)
  const [confirmingDefault, setConfirmingDefault] = useState(false)

  const isArchived = template.state === 'archived'
  const hasVersion = Boolean(template.currentVersion)
  const isConsent = values.category === 'informed_consent'
  const canChooseFlow = values.category === 'standalone_form' && values.audience === 'patient'
  // O tipo do dialeto não muda: só as categorias do mesmo tipo e do público escolhido.
  const categories = AUDIENCE_CATEGORIES[values.audience].filter(
    (category) => CATEGORY_TYPE[category] === template.type,
  )
  const dirty = JSON.stringify(values) !== JSON.stringify(saved)

  const nameError =
    values.name.trim().length < 3
      ? 'Dê um nome ao documento, com pelo menos 3 caracteres.'
      : values.name.trim().length > 120
        ? 'O nome pode ter no máximo 120 caracteres.'
        : null
  const procedureError =
    isConsent && values.procedure.trim() === ''
      ? 'Informe o procedimento a que este termo se aplica.'
      : null
  const serverFields = fieldErrors(update.error)
  const serverMessage =
    update.error && Object.keys(serverFields).length === 0 ? errorMessage(update.error) : null

  function set<K extends keyof Values>(key: K, value: Values[K]) {
    setValues((current) => ({ ...current, [key]: value }))
  }

  function chooseAudience(audience: Audience) {
    const allowed = AUDIENCE_CATEGORIES[audience].filter(
      (category) => CATEGORY_TYPE[category] === template.type,
    )
    setValues((current) => ({
      ...current,
      audience,
      category: allowed.includes(current.category) ? current.category : allowed[0]!,
      alsoInProcedure: audience === 'patient' ? current.alsoInProcedure : false,
    }))
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitted(true)
    if (nameError || procedureError || !dirty) return
    if (values.isDefault !== saved.isDefault) {
      setConfirmingDefault(true)
      return
    }
    save()
  }

  function save() {
    const input: Parameters<typeof update.mutate>[0] = {}
    if (values.name.trim() !== saved.name) input.name = values.name.trim()
    if (isConsent && values.procedure.trim() !== saved.procedure) {
      input.procedure = values.procedure.trim()
    }
    if (values.audience !== saved.audience) input.audience = values.audience
    if (values.category !== saved.category) input.category = values.category
    // Fora do termo avulso, o fluxo é o da categoria e a API o recalcula quando a categoria muda.
    if (
      values.category === 'standalone_form' &&
      (values.alsoInProcedure !== saved.alsoInProcedure || values.category !== saved.category)
    ) {
      input.selectableIn = values.alsoInProcedure ? ['procedure', 'standalone'] : ['standalone']
    }
    if (values.isDefault !== saved.isDefault) input.isDefault = values.isDefault
    update.mutate(input, {
      onSuccess: () => {
        setConfirmingDefault(false)
        toast('Dados do documento salvos')
      },
      onError: () => setConfirmingDefault(false),
    })
  }

  const categoryLabel = CATEGORY_LABELS[values.category]
  const audienceLabel = AUDIENCE_LABELS[values.audience]

  return (
    <form onSubmit={submit} noValidate className="flex max-w-[840px] flex-col gap-4">
      {isArchived ? (
        <AttentionBlock variant="lilac" title="Este documento está arquivado">
          Os dados não podem ser alterados. Para voltar a editar, restaure-o na aba Versões.
        </AttentionBlock>
      ) : null}

      {serverMessage ? (
        <ActionError title="Os dados não foram salvos" description={serverMessage} />
      ) : null}

      <FormBlock title="Identificação" note="O nome aparece nesta tela e na escolha das clínicas.">
        <div className="flex flex-col gap-4">
          <Field
            label="Nome"
            error={(submitted && nameError) || serverFields.name}
            hint={
              template.legacy?.legacyName
                ? `No sistema anterior: ${template.legacy.legacyName}`
                : undefined
            }
          >
            <Input
              value={values.name}
              maxLength={120}
              disabled={isArchived}
              onChange={(event) => set('name', event.target.value)}
            />
          </Field>
          {isConsent ? (
            <Field
              label="Procedimento"
              error={(submitted && procedureError) || serverFields.procedure}
              hint="Impresso no cabeçalho do termo. O vínculo com o catálogo é feito na aba Procedimentos."
            >
              <Input
                value={values.procedure}
                maxLength={120}
                disabled={isArchived}
                onChange={(event) => set('procedure', event.target.value)}
              />
            </Field>
          ) : null}
        </div>
      </FormBlock>

      <FormBlock
        title="Público e categoria"
        note="Decidem quem assina, em que fluxo o documento entra e quais variáveis o editor oferece."
      >
        <div className="flex flex-col gap-4">
          <div>
            <div className="eyebrow" id="template-data-audience-label">
              Público
            </div>
            <RadioGroup
              aria-labelledby="template-data-audience-label"
              className="mt-[6px] flex flex-wrap gap-x-6 gap-y-2"
              value={values.audience}
              disabled={isArchived || hasVersion}
              onValueChange={(value) => chooseAudience(value as Audience)}
            >
              {AUDIENCES.map((value) => (
                <label
                  key={value}
                  className="font-body text-lead text-ink min-h-touch-desk max-desk:min-h-touch flex cursor-pointer items-center gap-3 font-light"
                >
                  <Radio value={value} />
                  {AUDIENCE_LABELS[value]}
                </label>
              ))}
            </RadioGroup>
            <p className="font-body text-eyebrow text-secondary mt-1 leading-[1.5] font-light tracking-normal">
              {hasVersion
                ? 'O público não muda depois da primeira versão publicada. Para outro público, crie uma cópia em "+ Novo documento".'
                : 'Só muda até a primeira versão publicada.'}
            </p>
            {serverFields.audience ? (
              <p role="alert" className="font-body text-eyebrow text-plum mt-1 tracking-normal">
                {serverFields.audience}
              </p>
            ) : null}
          </div>

          <FieldGrid>
            <Field
              label="Categoria"
              error={serverFields.category}
              hint={
                categories.length === 1
                  ? 'É a única categoria deste tipo de documento para o público escolhido.'
                  : 'Só as categorias do mesmo tipo de documento: o tipo vem do texto e não muda.'
              }
            >
              <Select
                value={values.category}
                disabled={isArchived || categories.length === 1}
                onChange={(event) => set('category', event.target.value as Category)}
              >
                {categories.map((value) => (
                  <option key={value} value={value}>
                    {CATEGORY_LABELS[value]}
                  </option>
                ))}
              </Select>
            </Field>
          </FieldGrid>

          {canChooseFlow ? (
            <div>
              <ChoiceRow
                control={
                  <Checkbox
                    checked={values.alsoInProcedure}
                    disabled={isArchived}
                    onCheckedChange={(checked) => set('alsoInProcedure', checked === true)}
                  />
                }
                description="Além dos documentos avulsos, o termo aparece entre os escolhíveis da etapa 1 de gerar documentos de um atendimento."
              >
                Também no fluxo de procedimento
              </ChoiceRow>
              {serverFields.selectableIn ? (
                <p role="alert" className="font-body text-eyebrow text-plum mt-1 tracking-normal">
                  {serverFields.selectableIn}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </FormBlock>

      <FormBlock
        title="Padrão do catálogo"
        note="O padrão entra sozinho nas clínicas; quem já usa outro documento continua com o seu."
      >
        <ChoiceRow
          control={
            <Checkbox
              checked={values.isDefault}
              disabled={isArchived}
              onCheckedChange={(checked) => set('isDefault', checked === true)}
            />
          }
          description={
            EXCLUSIVE.includes(values.category)
              ? `Entra ativo nas clínicas novas e nas que ainda não têm ${categoryLabel.toLowerCase()}. Só um documento é o padrão desta categoria.`
              : `Entra ativo nas clínicas novas e nas que emitem para ${audienceLabel.toLowerCase()}.`
          }
        >
          Padrão de {categoryLabel.toLowerCase()}
        </ChoiceRow>
        {serverFields.isDefault ? (
          <p role="alert" className="font-body text-eyebrow text-plum mt-1 tracking-normal">
            {serverFields.isDefault}
          </p>
        ) : null}
      </FormBlock>

      {isArchived ? null : (
        <FormFooter
          secondary={
            <Button
              type="button"
              variant="outline"
              disabled={!dirty}
              onClick={() => {
                setValues(saved)
                setSubmitted(false)
                update.reset()
              }}
            >
              Cancelar
            </Button>
          }
          primary={
            <Button
              type="submit"
              disabled={!dirty}
              loading={update.isPending && !confirmingDefault}
              loadingLabel="Salvando…"
            >
              {dirty ? 'Salvar dados' : 'Nenhuma alteração para salvar'}
            </Button>
          }
        />
      )}

      <ConfirmDialog
        open={confirmingDefault}
        onOpenChange={setConfirmingDefault}
        tone="primary"
        title={
          values.isDefault
            ? `Tornar "${values.name.trim()}" o padrão de ${categoryLabel.toLowerCase()}?`
            : `Tirar "${values.name.trim()}" do padrão de ${categoryLabel.toLowerCase()}?`
        }
        effect={defaultEffect({
          isDefault: values.isDefault,
          isExclusive: EXCLUSIVE.includes(values.category),
          hasVersion,
          categoryLabel: categoryLabel.toLowerCase(),
          audienceLabel: audienceLabel.toLowerCase(),
          isRequired: isRequiredCategory(values.category),
        })}
        confirmLabel={values.isDefault ? 'Tornar padrão e salvar' : 'Tirar do padrão e salvar'}
        loading={update.isPending}
        loadingLabel="Salvando…"
        onConfirm={save}
      />
    </form>
  )
}

/** O efeito real de marcar ou desmarcar o padrão (RN-DOC-05, RN-DOC-49), e o que não muda. */
function defaultEffect(input: {
  isDefault: boolean
  isExclusive: boolean
  hasVersion: boolean
  categoryLabel: string
  audienceLabel: string
  isRequired: boolean
}): string {
  if (!input.isDefault) {
    return `As clínicas novas deixam de recebê-lo sozinhas. Onde ele já está ativo, continua ativo; nada emitido muda.${
      input.isRequired
        ? ` Sem outro padrão de ${input.categoryLabel}, desativar ou arquivar este documento passa a ser recusado onde a clínica ficaria sem ${input.categoryLabel}.`
        : ''
    }`
  }
  const when = input.hasVersion ? 'Agora' : 'Quando a primeira versão for publicada'
  if (input.isExclusive) {
    return `${when}, ele entra ativo nas clínicas novas e nas que emitem para ${input.audienceLabel} e ainda não têm ${input.categoryLabel}. Quem já usa outro continua com o seu: a troca é feita na aba Clínicas. O padrão atual desta categoria, se houver, deixa de ser padrão.`
  }
  return `${when}, ele entra ativo nas clínicas novas e nas que emitem para ${input.audienceLabel}. Onde foi desativado à mão, continua desativado; nada emitido muda.`
}
